<script setup lang="ts">
// Sunburst — concentric rings, root at centre. Author returns flat
// `{ parent, name, value }` rows; we pivot here using prototype-safe
// lookup tables (Map + Object.create(null)) per round-4 security M-2/M-5.
//
// Depth capped at 16 levels — anything deeper renders to the cap and
// we surface a `lucent-diagnostic` so authors see the truncation
// without the chart silently misbehaving.

import { computed, watch } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { SunburstChartSpec } from "../sbi/protocol";
import { CHART_COLORS, FONT_STACK_MONO, PALETTE } from "../theme.ts";
import { chartChrome, resolveCssVar, tooltipDefaults } from "./echarts-base";
import { NARROW } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";
import {
  pivotSunburstRows,
  sunburstMaxDepth,
  type SunburstTreeNode,
} from "./insight-pivot";

const SUNBURST_MAX_DEPTH = 16;

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

/** Rings thinner than this get no labels (the tooltip still names them). */
const MIN_LABELLED_RING_PX = 28;

const props = withDefaults(
  defineProps<{
    spec: SunburstChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
  (e: "diagnostic", code: string, detail: Record<string, unknown>): void;
}>();

const pivot = computed(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  return pivotSunburstRows(rows, props.spec.parent, props.spec.name, props.spec.value);
});

const truncatedRoots = computed<SunburstTreeNode[]>(() => {
  const max = sunburstMaxDepth(pivot.value.roots);
  if (max <= SUNBURST_MAX_DEPTH) return pivot.value.roots;
  // Truncate: prune subtrees deeper than the cap.
  function clip(node: SunburstTreeNode, depth: number): SunburstTreeNode {
    if (!node.children || depth >= SUNBURST_MAX_DEPTH) {
      const { children, ...rest } = node;
      void children;
      return rest;
    }
    return { ...node, children: node.children.map((c) => clip(c, depth + 1)) };
  }
  return pivot.value.roots.map((r) => clip(r, 1));
});

watch(
  pivot,
  (p) => {
    if (p.cyclicNames.length > 0) {
      emit("diagnostic", "sunburst_cycle_detected", {
        count: p.cyclicNames.length,
      });
    }
    if (p.orphanCount > 0) {
      emit("diagnostic", "sunburst_orphan_rows", { count: p.orphanCount });
    }
    if (sunburstMaxDepth(p.roots) > SUNBURST_MAX_DEPTH) {
      emit("diagnostic", "sunburst_depth_exceeded", {
        cap: SUNBURST_MAX_DEPTH,
      });
    }
  },
  { immediate: true },
);

// Ring labels at small radii: sectors under `minAngle` drop their label
// and labels truncate to the ring's thickness, instead of every sector
// overprinting its neighbours. Unmeasured keeps ECharts' defaults.
const ringLabel = computed(() => {
  const w = ctx.value.width;
  if (w <= 0) return { label: {}, show: true };
  const depth = Math.max(1, Math.min(SUNBURST_MAX_DEPTH, sunburstMaxDepth(truncatedRoots.value)));
  const ringPx = ((Math.min(w, height.value) / 2) * 0.9) / depth;
  return {
    label: {
      minAngle: w < NARROW ? 14 : 6,
      width: Math.max(16, Math.floor(ringPx - 6)),
      overflow: "truncate" as const,
    },
    show: ringPx >= MIN_LABELLED_RING_PX,
  };
});

const option = computed(() => ({
  ...chartChrome(ctx.value),
  tooltip: tooltipDefaults("item", ctx.value),
  series: [
    {
      type: "sunburst" as const,
      data: truncatedRoots.value,
      radius: [0, "90%"],
      sort: undefined,
      // Ring borders — embedder token first, canvas-safe hex fallback
      // (white = paper-coloured separators between rings).
      itemStyle: {
        borderColor:
          resolveCssVar("var(--lucent-sunburst-stroke)") ??
          CHART_COLORS.sunburstStroke,
        borderWidth: 1,
      },
      levels: [
        {},
        ...Array.from({ length: SUNBURST_MAX_DEPTH }, (_, i) => ({
          itemStyle: { color: PALETTE[i % PALETTE.length] },
          ...(ringLabel.value.show ? {} : { label: { show: false } }),
        })),
      ],
      // Ring labels — mono so they share the family with the rest of
      // the chart text. Colour inherits from `chartChrome(…).textStyle`
      // (warm card-ink); the prior `var(--lucent-sunburst-label-color)`
      // never resolved on the canvas paint path.
      label: { fontFamily: FONT_STACK_MONO, fontSize: 11, ...ringLabel.value.label },
    },
  ],
}));

const height = computed(() =>
  resolveChartHeight("sunburst", props.spec.height),
);

function onClick(p: { data?: unknown; treePathInfo?: unknown }) {
  if (!props.spec.id || !p?.data) return;
  const datum = p.data as { name?: string; value?: number };
  const pathInfo = (p.treePathInfo ?? []) as ReadonlyArray<{ name?: string }>;
  const path = pathInfo
    .map((n) => n.name ?? "")
    .filter((s): s is string => s.length > 0);
  emit("click-target", props.spec.id, {
    path,
    name: datum.name ?? "",
    value: datum.value ?? 0,
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
